import {UI} from "../../stem-core/ui/UIBase"
import {styleRule, styleRuleInherit, styleRuleCustom} from "../../stem-core/decorators/Style";
import {BaseBlinkApp} from "../BaseBlinkApp.jsx";
import {GlobalStyleSheet} from "../theme/GlobalStyleSheet.jsx";
import {Route, Router} from "../../stem-core/ui/Router";
import {iFrameConnection} from "../services/IFrameConnection";
import {IFrameMessages} from "../../blink-sdk/messaging/IFrameMessages";
import {iFrameState} from "../services/IFrameState";
import {authService} from "../../client/connection/services/AuthService.js";
import {Theme} from "../../stem-core/ui/style/Theme";
import {unwrapArray} from "../../stem-core/base/Utils";
import {PageTitleManager} from "../../stem-core/base/PageTitleManager";
import {CTA_URL} from "./PanelConstants";
import {BlinkGlobal, isDeepEqual, isSmallScreen} from "../Utils";
import {
    GATE_TYPE,
    IFRAME_APP_TYPE,
    PANEL_TYPE,
} from "../../blink-sdk/Constants";
import {Image} from "../../stem-core/ui/primitives/Image";
import {supportsPassiveEvents} from "../../blink-sdk/utils/Utils";
import {iFrameMerchantService} from "../services/IframeMerchantService";
import {initializeTheme} from "../theme/StyleConstants.js";
import {englishTranslationMap} from "../Messages";
import {apiClient} from "../../client/connection/BlinkApiClient.js";
import {GlobalState} from "../../stem-core/state/State";
import {iFrameAuthService, shiftToMerchantToken} from "../services/IFrameAuthService";
import {PanelCollapsed} from "./pages/panels/PanelCollapsed";
import {addScrimListeners} from "../widget/WidgetAnimation";
import {AnalyticsEventType, dispatchAnalyticsEvent} from "../../blink-sdk/utils/AnalyticsClient";
import {PanelToastStyle, Toast} from "../ui/Toast.jsx";
import {Flow} from "./flows/Flow.js";
import "./flows/FlowLogic.js"; // To make sure the flows are linked
import {BuildPanelRoutes} from "./pages/PanelRoutes.js";

export class PanelAppGlobalStyleSheet extends GlobalStyleSheet {
    @styleRuleCustom({selector: "body"})
    body = {
        margin: 0,
        userSelect: "text",
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        fontFamily: this.themeProps.FONT_FAMILY_DEFAULT,
        " *": {
            userSelect: () => isSmallScreen() ? "none" : "auto",
            "-webkit-tap-highlight-color": "transparent",
        },
        position: "absolute",
    };

    @styleRule
    panelApp = {
        overflow: "hidden !important",
        fontFamily: this.themeProps.FONT_FAMILY_DEFAULT,
        borderRadius: this.themeProps.MERCHANT_FRAME_BORDER_RADIUS,
        background: "transparent",
        color: this.themeProps.TEXT_PRIMARY_COLOR,
        fontSize: this.themeProps.FONT_SIZE_NORMAL,
        lineHeight: this.themeProps.GENERAL_LINE_HEIGHT,
        maxWidth: "100%",
        maxHeight: "100%"
    };

    @styleRule
    popupGate = {
        margin: "auto",
        maxWidth: "90%",
        maxHeight: "90%"
    };

    @styleRuleInherit
    a = {
        fontWeight: this.themeProps.FONT_WEIGHT_BOLD,
    };

    @styleRuleCustom({selector: "b"})
    b = {
        fontWeight: this.themeProps.FONT_WEIGHT_BOLD,
    };

    @styleRuleInherit
    allInputs = {
        fontFamily: this.themeProps.FONT_FAMILY_DEFAULT,
    };
}

export class PanelApp extends BaseBlinkApp {
    merchantFonts = [];
    sizeStyle = {};


    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        const styleSheet = PanelAppGlobalStyleSheet.getInstance();

        attr.addClass(styleSheet.panelApp);

        if (iFrameState.gateType === GATE_TYPE.popup || iFrameState.gateType == GATE_TYPE.popupGate) {
            attr.addClass(styleSheet.popupGate);
        }

        attr.setStyle(this.sizeStyle);
    }

    applyMerchantTheme() {
        this.addMerchantFonts();
        // TODO @optimize we probably only want to add the new merchant fields
        initializeTheme(iFrameState.theme);
    }

    applyMerchantMessages() {
        for (const [key, value] of Object.entries(iFrameState.messages)) {
            englishTranslationMap.set(key, value);
        }
    }

    applyMerchantConfig() {
        this.applyMerchantTheme(); // TODO is this only limited to the scope of this app? These are Global
        this.applyMerchantMessages();
        Theme.Global.updateStyleSheets();
        // TODO This should probably be done in initializeTheme()
        Theme.Global.register(Toast, PanelToastStyle);
    }

    constructor(...args) {
        super(...args);

        BlinkGlobal.appInstance = this;

        const route = iFrameState.handleFlowInline() ? PANEL_TYPE.empty : CTA_URL;
        this.initRouter(route);

        this.applyMerchantConfig();
    }

    initRouter(initialPage) {
        // The router in this app shouldn't alter the browser history.
        Router.useLocalHistory = true;
        Router.localHistory = ["/" + initialPage];

        // TODO @flow2 probably remove this shit, it doesn't make any sense
        Router.changeToCustomPanel = function() {
            // The panel rendered will use iFrameState.iframeParams.panelContent. That value has some defaults depending on the
            // panel type (PANEL_CONTENT[panelType]). Look up the "composePanelOptions" method in CreatePanelDefaultOptions.
            Router.changeURL(CTA_URL);
        }

        Router.globalSetURL = function(urlParts) {
            urlParts = unwrapArray(urlParts);
            const page = this.getPageToRender(urlParts);

            if (!page) return;

            const activePage = this.getActive();
            const app = BlinkGlobal.appInstance;

            if (page instanceof PanelCollapsed) {
                app.setStyle("pointerEvents", "none");
            } else {
                app.setStyle("pointerEvents", "initial");
            }

            if (activePage !== page) {
                app.applyMerchantConfig(); // Just for good measure. @cleanup it seems to be applied by a later listener.
                if (activePage) {
                    activePage.dispatch("urlExit");
                }
                this.setActive(page);
                page.dispatch("urlEnter");

                const urlRoute = urlParts.join("/");
                if (urlRoute !== "empty") {
                    if (iFrameState.gateType === GATE_TYPE.popup || iFrameState.gateType == GATE_TYPE.popupGate) {
                        dispatchAnalyticsEvent(AnalyticsEventType.POPUP_PAGE_CHANGE, {
                            page: urlRoute,
                            flowType: iFrameState.panelType
                        });
                    }
                    iFrameConnection.sendToSDK(IFrameMessages.ROUTE_CHANGE, {route: urlRoute});
                }
            } else {
                page.dispatch("urlReload");
            }

            if (page?.pageTitle) {
                PageTitleManager.setTitle(page.pageTitle);
            }

            this.dispatch("change", urlParts, page, activePage);

            if (iFrameState.iframeParams.inlineFlow) {
                iFrameConnection.sendToSDK(IFrameMessages.SCROLL_IFRAME_TOP, {windowScroll: {top: 0}});
            }
        }
    }

    addMerchantFonts() {
        const styleElement = document.createElement("style");
        for (const font of iFrameState.fonts) {
            // TODO @cleanup This is stupid
            if (!this.merchantFonts.find(f => isDeepEqual(f, font))) {
                const {family, style, src, weight, unicodeRange} = font;
                styleElement.appendChild(document.createTextNode(`
                @font-face{
                    font-family: ${family};
                    src: ${src};
                    ${style ? `font-style: ${style};` : ""}
                    ${weight ? `font-weight: ${weight};` : ""}
                    ${unicodeRange ? `unicode-range: ${unicodeRange}; ` : ""}
                }`));
                this.merchantFonts.push(font);
            }
        }
        if (styleElement.innerHTML.length) {
            document.head.appendChild(styleElement);
        }
    }

    static initializeGlobalStyle() {
        PanelAppGlobalStyleSheet.initialize();
    }

    static addWsListener(streamName) {
        apiClient.addWebsocketListener(streamName, async event => {
            if (event.type === "logout") {
                if (iFrameAuthService.merchantToken) {
                    shiftToMerchantToken();
                } else {
                    authService.clearCurrentInfo();
                }
            } else {
                GlobalState.applyEvent(event);
            }
        });
    }

    getRouterOptions() {
        let routerStyle = {
            [GATE_TYPE.popup]: {
                height: "100%",
                width: "100%",
                overflow: "auto",
            },
            [GATE_TYPE.popupGate]: {
                height: "100%",
                width: "100%",
                overflow: "auto",
            },
            [GATE_TYPE.banner]: {
                width: "100%",
            },
            [GATE_TYPE.cover]: {
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            },
            // It actually works when gateType is null
            "null": {
                width: "100%",
                display: "flex",
                overflow: "auto",
                justifyContent: "center",
            }
        }

        return {style: routerStyle[iFrameState.gateType]};
    }

    // TODO @cleanup delete this?
    // Render these invisible elements so the fetches execute earlier.
    getStaticInvisibleElements() {
        const merchant = iFrameMerchantService.getMerchant();

        // This is so that the elements are under no circumstance visible.
        return (
            <div style={{opacity: 0, pointerEvents: "none", zIndex: -1, position: "absolute", top: -1e4, left: -1e4}}>
                {merchant && <Image src={merchant.wideLogoUrl}/>}
                <div style={{fontStyle: "italic"}}>a</div>
                <div style={{fontWeight: Theme.props.FONT_WEIGHT_BOLD}}>a</div>
                <div>a</div>
            </div>
        );
    }

    getRoutes() {
        return new Route([], null, BuildPanelRoutes(), {});
    }

    preventScrollPropagation() {
        const node = document.documentElement;
        node.setAttribute("tabindex", 0);

        const scrollEventOptions = supportsPassiveEvents() ? {passive: false} : false;

        node.addEventListener("wheel", e => e.stopPropagation(), scrollEventOptions);
        node.addEventListener("touchmove", e => e.stopPropagation(), scrollEventOptions);
    }

    hideScrim() {
        this.redraw();
        Object.assign(document.documentElement.style, {
            background: "transparent",
        });
    }

    showScrim() {
        Object.assign(document.documentElement.style, {
            background: PanelAppGlobalStyleSheet.getInstance().themeProps.MERCHANT_SCRIM_BACKGROUND,
        });
    }

    onMount() {
        super.onMount();

        // TODO @flow what should happen here?
        Flow.updateActiveFlow();
        iFrameState.addChangeListener(() => Flow.updateActiveFlow());

        iFrameConnection.addListener(IFrameMessages.ROUTE_CHANGE, ({url}) => {
            if (url === PANEL_TYPE.empty) {
                Flow.clearActiveFlow();
            }
        });

        addScrimListeners(IFRAME_APP_TYPE.panel);
        this.preventScrollPropagation();

        this.attachChangeListener(iFrameState, () => {
            this.applyMerchantConfig();
            this.redraw();
        });

        if (iFrameState.gateType === GATE_TYPE.popup && Router.getCurrentPath() !== "/" + PANEL_TYPE.empty) {
            this.showScrim();
        }

        iFrameConnection.addListener(IFrameMessages.ROUTE_CHANGE, ({url}) => {
            if (url) {
                Router.changeURL(url);
            } else {
                // If the page is undefined, it will be handled inside the flow logic.
            }
            // Order matters. We need the router paths to be correct.
            if (url === PANEL_TYPE.empty) {
                this.hideScrim();
            } else if (iFrameState.gateType === GATE_TYPE.popup) {
                this.showScrim();
            }
        });

        // Expose these for debugging. Not exposed directly on window to not allow code to work without proper imports.
        window.PanelDebug = {
            Flow,
            Router,
            iFrameState,
        }
    }
}
